<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
    <div :class="['task-field', errorMessages ? 'field-error' : '']">
        <Dialog
            v-if="showDialog"
            :value="showDialog"
            :header-text="$t('Create new topic')"
            :width="360"
            header_classes="black white--text"
            persistent
            @close_modal="close_modal"
        >
            <v-container v-if="form_data" class="px-0">
                <v-text-field
                    v-model="form_data.name"
                    :label="$t('Name')"
                    outlined
                    :error-messages="form_data.name_error"
                ></v-text-field>
            </v-container>
            <template slot="footer">
                <v-spacer/>
                <v-btn text @click="close_modal">
                    {{ $t("Close") }}
                </v-btn>
                <v-btn class="primary inverted--text" :loading="loading" @click="save_topic">
                    {{ $t("Add") }}
                </v-btn>
            </template>
        </Dialog>
        <div class="global-label" :class="$vuetify.breakpoint.mdAndUp ? 'mt-2' : ''">{{ label }}</div>
        <div>
            <v-menu v-model="is_menu_open" :transition="false" offset-y right :close-on-content-click="false">
                <template v-slot:activator="{ on }">
                    <slot v-bind="{ on, is_menu_open }"></slot>
                </template>
                <v-card>
                    <v-text-field
                        ref="filterBox"
                        v-model="keyword"
                        :placeholder="$t('Find topic...')"
                        hide-details
                        class="menu-filter-text mt-0 pt-0 body-2"
                        @keypress.enter.esc="$emit('tab_next')"
                    ></v-text-field>
                    <v-list dense style="max-height: 333px; max-width: 273px; overflow: auto;">
                        <v-list-item
                            v-for="item in filtered_list"
                            :key="item.id"
                            style="min-height: 32px;"
                            @click="toggle_selection($event, item.id)"
                        >
                            <v-list-item-content>
                                <v-list-item-title
                                    class="font-weight-regular subtitle-2 d-flex align-center"
                                >
                                    <div class="text-truncate">
                                        {{ item.name }}
                                    </div>
                                    <div
                                        v-if="!item.billable"
                                        class="flex-shrink-0"
                                    >
                                        <icon
                                            icon="non_billable"
                                            size="20"
                                            color="error"
                                        />
                                    </div>
                                </v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action style="margin: 0;">
                                <icon
                                    v-if="topic_id === item.id"
                                    icon="Check"
                                    color="primary"
                                />
                            </v-list-item-action>
                        </v-list-item>
                    </v-list>
                    <div v-if="can_add_topics" class="menu-add-new">
                        <v-btn text small color="primary" @click="new_topic">
                            <icon icon="Plus" size="18"/>
                            <span class="ml-2">{{ $t("New topic") }}</span>
                        </v-btn>
                    </div>
                </v-card>
            </v-menu>

            <div v-if="!!errorMessages" :class="['details', errorMessages ? 'error--text' : '']">
                {{ errorMessages }}
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import {ref, computed, watch} from "@vue/composition-api";
import ClientAvatar from "../components/ClientAvatar.vue";
import ClientAvatarListItem from "../components/ClientAvatarListItem.vue";
import Dialog from "../components/Dialog.vue";
import TopicModule from "../store/modules/topic";
import AppModule from "../store/modules/app";
import AccountModule from "../store/modules/account";

export default {
    name: "TopicsField",
    components: {
        ClientAvatar,
        ClientAvatarListItem,
        Dialog
    },
    props: {
        label: {
            type: String,
            default: null
        },
        value: {
            type: Number,
            required: false,
            default: 0
        },
        accountId: {
            validator: prop => typeof prop === "number" || prop === null,
            default: null
        },
        items: {
            type: Array,
            required: true
        },
        errorMessages: {
            type: String,
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    setup(props, ctx) {
        const {saveTopic} = TopicModule();
        const {handleErrorResponse, clearValidationErrors, appendClassifiersCache, rights_masks} = AppModule();
        const {accounts_info} = AccountModule();
        const is_menu_open = ref(false);
        const keyword = ref(null);
        const filterBox = ref(null);
        const topic_id = ref(props.value);
        const showDialog = ref(false);
        const form_data = ref(null);
        const loading = ref(false);

        const filtered_list = computed(function () {
            if (!keyword.value) return props.items;
            let key = keyword.value.toLowerCase();
            return props.items.filter(x => x.name.toLowerCase().indexOf(key) >= 0);
        });

        const can_add_topics = computed(function () {
            if (props.accountId == null || props.accountId === 0) return true;
            let accounts = accounts_info.value.filter(x => x.id === props.accountId);
            if (accounts.length === 0) return false;
            if ((accounts[0].rights & rights_masks.value.manage_account) === rights_masks.value.manage_account)
                return true;
            if (accounts[0].data === undefined || accounts[0].data.topic_management_disabled_for_users === undefined)
                return true;
            return accounts[0].data.topic_management_disabled_for_users !== 1;
        });

        const toggle_selection = function (e, id) {
            if (topic_id.value === id) {
                topic_id.value = null;
            } else {
                topic_id.value = id;
                is_menu_open.value = false;
                keyword.value = null;
            }
            ctx.emit("change", topic_id.value);

            // Assume enter is pressed
            if (e.clientX === 0 && e.clientY === 0) {
                ctx.emit("tab_next");
            }
        };

        const new_topic = function () {
            if (props.accountId == null || props.accountId === 0) {
                ctx.emit("select_account");
                return;
            }

            form_data.value = Vue.prepare_form_data({name: "", account_id: props.accountId}, {name: "text"});
            showDialog.value = true;
            is_menu_open.value = false;
        };
        const save_topic = function () {
            loading.value = true;
            clearValidationErrors(form_data.value);
            saveTopic(form_data.value)
                .then(function (resp) {
                    appendClassifiersCache("topics", resp, props.accountId);
                    loading.value = false;
                    showDialog.value = false;
                    ctx.emit("change", resp.id);
                })
                .catch(function (ex) {
                    loading.value = false;
                    handleErrorResponse(form_data.value, ex);
                });
        };

        const focus = function () {
            if (!is_menu_open.value) is_menu_open.value = true;
        };
        const blur = function () {
            if (is_menu_open.value) is_menu_open.value = false;
        };

        const close_modal = function () {
            showDialog.value = false;
        };

        watch(is_menu_open, function (val, prev_val) {
            if (prev_val === undefined) return;

            if (val) {
                setTimeout(function () {
                    filterBox.value.focus();
                }, 100);
            } else if (props.value !== topic_id.value) {
                ctx.emit("change", topic_id.value);
            }
        });

        watch(
            () => props.value,
            function (val) {
                topic_id.value = val != null ? val : null;
            }
        );

        return {
            topic_id,
            is_menu_open,
            keyword,
            filtered_list,
            filterBox,
            showDialog,
            form_data,
            loading,
            can_add_topics,
            focus,
            blur,
            toggle_selection,
            new_topic,
            close_modal,
            save_topic
        };
    }
};
</script>
